/**
 * focusables.ts — "what can a Tab land on inside this box", for a modal that
 * keeps the keyboard inside itself.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * ONE SELECTOR FOR THE MODALS THAT CONTAIN TAB (review, 2026-09-25). The
 * celebration (`checkout/Celebration.tsx`) and `DialogShell`'s opt-in
 * `trapFocus` both read it from here. `tracking/ConsentBanner.tsx` still holds
 * its own copy of the same selector: it ships to production on every tracked
 * page, and moving it was not worth touching the cookie prompt for.
 *
 * DELIBERATELY WIDER THAN ANY ONE MODAL'S CONTENT TODAY. Containment that only
 * knows the controls that happened to be there when it was written silently
 * stops containing the day someone adds a link or a field.
 *
 * DOM order, disabled controls left out. A `tabIndex=-1` panel is left out too,
 * so the panel itself is never one of the stops.
 */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function focusablesIn(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * Keep a Tab keydown inside `panel`: from the last stop Tab wraps to the first,
 * from the first Shift+Tab wraps to the last, and from anywhere that is not one
 * of the stops (the panel itself, or somewhere outside it) Tab goes to the first
 * and Shift+Tab to the last. With no stops at all the panel keeps focus. Any key
 * but Tab is left alone. Between stops inside the panel the browser moves focus
 * itself, so this only acts at the edges.
 */
export function containTab(event: KeyboardEvent, panel: HTMLElement): void {
  if (event.key !== 'Tab') return;
  const items = focusablesIn(panel);
  if (items.length === 0) {
    event.preventDefault();
    panel.focus();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const at = items.indexOf(document.activeElement as HTMLElement);
  if (at === -1 || (event.shiftKey ? at === 0 : at === items.length - 1)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  }
}
